import { watch } from 'vue';
import { useRoute } from 'vue-router';

/**
 * First-party tracking composable. Handles:
 *   1. Pageview pings      → POST /api/track.php       (unchanged)
 *   2. Time on page        → POST /api/events.php  type=time
 *   3. Scroll depth        → POST /api/events.php  type=scroll
 *   4. Outbound link clicks → POST /api/events.php  type=outbound
 *   5. CTA clicks (manual) → POST /api/events.php  type=cta  (via trackCta())
 *
 * All signals are GDPR consent-gated (localStorage.bmcs_consent === 'grant').
 * Server-side consent + rate-limit gates also apply independently.
 * Silent on failure — missed events must never disrupt the user experience.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_TIME_MS = 30 * 60 * 1000; // 30 min cap — over that is a tab left open
const SCROLL_DEBOUNCE_MS = 250;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function hasConsent(): boolean {
  try {
    return localStorage.getItem('bmcs_consent') === 'grant';
  } catch {
    return false;
  }
}

function getSessionId(): string {
  return ensureSession();
}

function beaconEvent(payload: {
  type: 'time' | 'scroll' | 'outbound' | 'cta';
  path: string;
  value?: number;
  label?: string;
  session?: string;
}): void {
  if (!hasConsent()) return;
  const body = JSON.stringify(payload);
  if (navigator.sendBeacon) {
    navigator.sendBeacon(
      '/api/events.php',
      new Blob([body], { type: 'application/json' }),
    );
  } else {
    fetch('/api/events.php', {
      method: 'POST',
      body,
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
    }).catch(() => {
      /* silent */
    });
  }
}

// ---------------------------------------------------------------------------
// Pageview tracking (unchanged behaviour)
// ---------------------------------------------------------------------------

function sendPageview(path: string, sessionId: string): void {
  if (!hasConsent()) return;
  const body = JSON.stringify({
    path,
    referrer: document.referrer || '',
    session: sessionId,
  });
  if (navigator.sendBeacon) {
    navigator.sendBeacon('/api/track.php', new Blob([body], { type: 'application/json' }));
  } else {
    fetch('/api/track.php', {
      method: 'POST',
      body,
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
    }).catch(() => {
      /* silent */
    });
  }
}

// ---------------------------------------------------------------------------
// Time-on-page + scroll depth state (module-level so listeners survive
// the composable call going out of scope)
// ---------------------------------------------------------------------------

let enterTime = 0;
let currentPath = '';
let maxScrollPct = 0;
let scrollTimer: ReturnType<typeof setTimeout> | null = null;
let behaviourListenersAttached = false;

function flushBehaviourSignals(session: string): void {
  if (!currentPath) return;

  // Time on page
  if (enterTime > 0) {
    const elapsed = Math.min(Date.now() - enterTime, MAX_TIME_MS);
    if (elapsed > 0) {
      beaconEvent({ type: 'time', path: currentPath, value: elapsed, session });
    }
  }

  // Scroll depth — only send if the user actually scrolled
  if (maxScrollPct > 0) {
    beaconEvent({ type: 'scroll', path: currentPath, value: maxScrollPct, session });
  }
}

function resetBehaviourState(path: string): void {
  currentPath = path;
  enterTime = Date.now();
  maxScrollPct = 0;
}

function onScroll(): void {
  const scrollable = document.documentElement.scrollHeight - window.innerHeight;
  if (scrollable <= 0) return;
  const pct = Math.round((window.scrollY / scrollable) * 100);
  if (pct > maxScrollPct) maxScrollPct = pct;
}

function attachBehaviourListeners(getSession: () => string): void {
  if (behaviourListenersAttached) return;
  behaviourListenersAttached = true;

  // --- Scroll depth (passive, debounced) ---
  window.addEventListener(
    'scroll',
    () => {
      if (scrollTimer !== null) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(onScroll, SCROLL_DEBOUNCE_MS);
    },
    { passive: true },
  );

  // --- Flush on tab hide / page close ---
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushBehaviourSignals(getSession());
    }
  });
  window.addEventListener('pagehide', () => {
    flushBehaviourSignals(getSession());
  });

  // --- Outbound link clicks (delegated) ---
  document.addEventListener('click', (e: MouseEvent) => {
    if (!hasConsent()) return;
    const target = e.target as Element | null;
    const anchor = target?.closest('a') as HTMLAnchorElement | null;
    if (!anchor) return;

    let href = '';
    try {
      href = anchor.href; // absolute URL resolved by browser
    } catch {
      return;
    }

    if (!href) return;

    let url: URL;
    try {
      url = new URL(href);
    } catch {
      return; // not a valid URL
    }

    if (url.host && url.host !== window.location.host) {
      // External — fire outbound event. Do NOT preventDefault.
      beaconEvent({
        type: 'outbound',
        path: window.location.pathname,
        value: 0,
        label: href,
        session: getSession(),
      });
    }
  });
}

// ---------------------------------------------------------------------------
// Public composable
// ---------------------------------------------------------------------------

export function useTracking() {
  if (import.meta.env.DEV) return;
  if (typeof window === 'undefined') return;

  const route = useRoute();
  const sessionId = ensureSession();

  attachBehaviourListeners(() => sessionId);

  watch(
    () => route.fullPath,
    (newPath, oldPath) => {
      // Flush behaviour signals for the page we're leaving (not on initial load)
      if (oldPath !== undefined) {
        flushBehaviourSignals(sessionId);
      }

      // Reset state for the new page
      resetBehaviourState(newPath);

      // Fire pageview
      sendPageview(newPath, sessionId);
    },
    { immediate: true },
  );
}

// ---------------------------------------------------------------------------
// CTA tracker — call from any view/component button click handler
// ---------------------------------------------------------------------------

/**
 * Track an explicit CTA interaction.
 *
 * Usage (in a view):
 *   import { trackCta } from '@/composables/useTracking'
 *   // inside a @click handler:
 *   trackCta('Talk To Us')
 */
export function trackCta(label: string): void {
  if (typeof window === 'undefined') return;
  if (import.meta.env.DEV) return;
  beaconEvent({
    type: 'cta',
    path: window.location.pathname,
    value: 0,
    label,
    session: getSessionId(),
  });
}

// ---------------------------------------------------------------------------
// Session helper (unchanged)
// ---------------------------------------------------------------------------

function ensureSession(): string {
  const KEY = 'bmcs_session';
  const existing = sessionStorage.getItem(KEY);
  if (existing) return existing;
  // sessionStorage lives for the tab — a "session" here = one browser tab lifecycle.
  const fresh = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  sessionStorage.setItem(KEY, fresh);
  return fresh;
}
